import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'shared/contexts/AuthContext';
import {
  getActiveExpertJobAssignmentsForExpert,
  subscribeToActiveExpertJobMetrics,
} from 'domains/jobs/data/expertJobs';
import { subscribeToUnreadMessagesForUser } from 'domains/messaging/data/messageBoard';

export interface ExpertLayoutViewModel {
  assignedJobsCount: number;
  unreadMessagesCount: number;
  initialize: () => void;
}

/**
 * Live counts displayed by the expert navigation. Job-metric snapshots act as
 * the assignment trigger, while the exact assignment read preserves the same
 * eligibility rules as the My Jobs page. Message counts are maintained by the
 * message-board listener, which watches channel activity and read receipts.
 */
export function useExpertLayoutViewModel(): ExpertLayoutViewModel {
  const { userAccount } = useAuth();
  const [assignedJobsCount, setAssignedJobsCount] = useState(0);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const cleanupRef = useRef<(() => void) | null>(null);

  const initialize = useCallback(() => {
    cleanupRef.current?.();
    cleanupRef.current = null;

    const expert = userAccount;
    if (!expert?.id) {
      setAssignedJobsCount(0);
      setUnreadMessagesCount(0);
      return;
    }

    let active = true;
    let jobRefreshSequence = 0;

    const refreshJobCount = async (): Promise<void> => {
      const sequence = ++jobRefreshSequence;
      try {
        const assignments = await getActiveExpertJobAssignmentsForExpert(expert);
        if (active && sequence === jobRefreshSequence) {
          setAssignedJobsCount(assignments.eligibleEjmSummaries.length);
        }
      } catch {
        // Keep the listener-derived value when the verification read is
        // temporarily unavailable; the next snapshot will retry it.
      }
    };

    const unsubscribeJobs = subscribeToActiveExpertJobMetrics(
      expert.id,
      (metrics) => {
        if (!active) return;
        // Give the sidebar an immediate value from the live snapshot, then
        // reconcile it with the full task-eligibility pass.
        setAssignedJobsCount(metrics.filter((metric) => metric.isEligibleForTaskAssignment).length);
        void refreshJobCount();
      },
      () => {
        // Keep the last known count when a listener reconnects.
      },
    );
    const unsubscribeMessages = subscribeToUnreadMessagesForUser(
      expert.id,
      (count) => {
        if (active) setUnreadMessagesCount(count);
      },
      () => {
        // Keep the last known count when a listener reconnects.
      },
    );

    cleanupRef.current = () => {
      active = false;
      unsubscribeJobs();
      unsubscribeMessages();
    };
  }, [userAccount]);

  useEffect(() => {
    initialize();
    return () => {
      cleanupRef.current?.();
      cleanupRef.current = null;
    };
  }, [initialize]);

  return { assignedJobsCount, unreadMessagesCount, initialize };
}
